import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Chip,
  Spinner,
  Button
} from "@heroui/react";
import { useCreatePreview } from '../hooks/useCreatePreview';

const CampaignPreview = ({
    emailValidations,
    cancelPreview,
    cancelForm,
    mailer_content,
    user,
    typos,
    subject,
    tone,
    prompt_inject,
    tip_jar_message,
    variability,
    subject_variability,
    vanity_url,
    toRecipients,
    ccRecipients,
    bccRecipients,
    stompClient,
    footer,
    campaign,
    setBanner,
    language
}) => {
  const [loading, setLoading] = useState(true);
  // We set previewSubject and body when we poll and finally get the preview data
  const [body, setBody] = useState();
  const [previewSubject, setPreviewSubject] = useState();
  const previewId = useCreatePreview(subject, mailer_content, tone, variability, subject_variability, campaign, prompt_inject, language);

  const genStompTopicPrefix = () => {
    return window.location.hostname.split('.')[0];
  };

  useEffect(() => {
    if (!previewId) return; // Don't subscribe until we have a preview ID

    const subscription = stompClient?.subscribe(`/topic/users/${genStompTopicPrefix()}_${user?._id || user?.id}_campaigns`, (message) => handleStompMessage(message));

    return () => {
      subscription?.unsubscribe();
    };
  }, [stompClient, user?._id, user?.id, previewId]);

  const handleStompMessage = async (m) => {
    const message = JSON.parse(m.body);
    console.log("[handleStompMessage]: " + JSON.stringify(message));
    console.log("Current previewId:", previewId);
    console.log("Message preview_update exists:", !!message['preview_update']);

    if (message['preview_update']) {
      console.log("Message campaign_preview_id:", message['preview_update']['campaign_preview_id']);
      console.log("IDs match:", message['preview_update']['campaign_preview_id'] === previewId);
    }

    if (message['preview_update'] && previewId && message['preview_update']['campaign_preview_id'] === previewId) {
      console.log("Processing preview update for matching ID");
      if (message['preview_update']['mailer_content']) {
        console.log("Setting body from STOMP message");
        setBody(message['preview_update']['mailer_content']);
      }
      if (message['preview_update']['subject']) {
        console.log("Setting previewSubject from STOMP message");
        setPreviewSubject(message['preview_update']['subject']);
      }
    } else {
      console.log("Skipping preview update - no match or missing data");
    }
  };

  // Check if both body and subject are loaded to stop loading spinner
  useEffect(() => {
    console.log("useEffect: body=", !!body, "previewSubject=", !!previewSubject, "loading=", loading);
    if (body && previewSubject) {
      console.log("Both body and subject ready, setting loading to false");
      setLoading(false);
    }
  }, [body, previewSubject]);

  const getEmailValidationColor = (email) => {
    if (emailValidations && emailValidations.length > 0) {
      const thisValidation = emailValidations.filter(e => e?.address === email);
      if (thisValidation && thisValidation.length > 0) {
        if (thisValidation[0].status === 'valid') {
          return 'success';
        } else if (thisValidation[0].status === 'catch-all' || thisValidation[0].status === 'do_not_mail') {
          return 'warning';
        } else {
          return 'danger';
        }
      }
    }
    return 'default';
  };

  return createPortal(
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 99999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#4d5c76',
        borderRadius: '12px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        width: '800px',
        overflowY: 'auto'
      }}>
        {/* Diagonal Watermark */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%) rotate(-45deg)',
          fontSize: '120px',
          fontWeight: 'bold',
          color: 'rgba(240, 240, 240, 0.1)',
          zIndex: 1,
          pointerEvents: 'none',
          userSelect: 'none',
          whiteSpace: 'nowrap'
        }}>
          PREVIEW
        </div>

        <div style={{ position: 'relative', zIndex: 2, padding: '20px 30px 10px 30px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{display: 'flex', flexDirection: 'column'}}>
            <span style={{fontSize: '16px', fontWeight: 'bold', color: '#f0f0f0'}}>
              Preview of a sample email for:
              <br />
              <span style={{marginTop: '5px', fontSize: '15px', fontWeight: 'bold', display: 'flex'}}>
                https://app.ActivistMailer.com/{vanity_url || <i style={{color: '#f0f0f0'}}>Not specified yet</i>}
              </span>
            </span>
          </div>
        </div>
        <div style={{ position: 'relative', zIndex: 2, padding: '20px 30px' }}>
          { (loading && (!body && !previewSubject))
            ? <div>
                <Spinner />
                <div style={{width: '480px', display: 'inline-block'}}>
                  <span style={{paddingLeft: '6px', fontSize: '14px', color: '#f0f0f0'}}>
                    Processing. This may take a few moments.
                  </span>
                </div>
              </div>
            :
              ''
          }
          <p />
          <div className='helper'>
            <div style={{display: 'flex', flexWrap: 'wrap', alignContent: 'start', padding: '6px', width: '100%'}}>
              <span style={{fontWeight: 'bold', color: '#f0f0f0', marginRight: '8px'}}>To:</span>
              { toRecipients.slice(0, 20).map(r =>
                <Chip key={r} color={getEmailValidationColor(r)} variant="bordered" style={{margin: '2px'}}>
                  {r}
                </Chip>
              )}
              {toRecipients.length > 20 && (
                <Chip variant="bordered" color="default" style={{margin: '2px', backgroundColor: '#f0f0f0', color: '#666'}}>
                  and {(toRecipients.length - 20).toLocaleString()} more To recipients
                </Chip>
              )}
            </div>

            <div style={{display: 'flex', flexWrap: 'wrap', alignContent: 'start', padding: '6px', width: '100%'}}>
              <span style={{fontWeight: 'bold', color: '#f0f0f0', marginRight: '8px'}}>Cc:</span>
              { ccRecipients.slice(0, 20).map(r =>
                <Chip key={r} color={getEmailValidationColor(r)} variant="bordered" style={{margin: '2px'}}>
                  {r}
                </Chip>
              )}
              {ccRecipients.length > 20 && (
                <Chip variant="bordered" color="default" style={{margin: '2px', backgroundColor: '#f0f0f0', color: '#666'}}>
                  and {(ccRecipients.length - 20).toLocaleString()} more CC recipients
                </Chip>
              )}
            </div>

            <div style={{display: 'flex', flexWrap: 'wrap', alignContent: 'start', padding: '6px', width: '100%'}}>
              <span style={{fontWeight: 'bold', color: '#f0f0f0', marginRight: '8px'}}>Bcc:</span>
              { bccRecipients.slice(0, 20).map(r =>
                <Chip key={r} color={getEmailValidationColor(r)} variant="bordered" style={{margin: '2px'}}>
                  {r}
                </Chip>
              )}
              {bccRecipients.length > 20 && (
                <Chip variant="bordered" color="default" style={{margin: '2px', backgroundColor: '#f0f0f0', color: '#666'}}>
                  and {(bccRecipients.length - 20).toLocaleString()} more BCC recipients
                </Chip>
              )}
            </div>
          </div>

          <div className='helper' style={{marginTop: '16px'}}>
            <span style={{fontWeight: 'bold', color: '#f0f0f0'}}>Subject:</span>
            {(previewSubject && previewSubject !== '') ?
              <pre style={{whiteSpace: 'pre-wrap', color: '#f0f0f0', marginTop: '8px', padding: '8px', backgroundColor: 'rgba(32, 32, 32, 0.3)', borderRadius: '4px'}}>
                {previewSubject}
              </pre> :
              <div style={{marginTop: '8px'}}>
                <Spinner color="secondary" />
              </div>
            }
          </div>

          <div className='helper' style={{marginTop: '16px'}}>
            <span style={{fontWeight: 'bold', color: '#f0f0f0'}}>Body:</span>
            {((body && body !== '') || variability === 0) ?
              <pre style={{
                whiteSpace: 'pre-wrap',
                color: '#f0f0f0',
                marginTop: '8px',
                padding: '12px',
                backgroundColor: 'rgba(32, 32, 32, 0.3)',
                borderRadius: '4px',
                fontSize: 'medium',
                maxWidth: '100%',
                overflow: 'auto'
              }}>
                {variability === 0 ? mailer_content : body}
              </pre> :
              <div style={{marginTop: '8px'}}>
                <Spinner color="secondary" />
              </div>
            }
          </div>

          { (footer && body && body !== '') &&
            <div style={{marginTop: '16px', padding: '8px', borderTop: '1px solid rgba(240, 240, 240, 0.2)'}}>
              <span style={{fontSize: '14px', color: '#f0f0f0', fontStyle: 'italic'}}>
                Mailed with the help of ActivistMailer.com
              </span>
            </div>
          }

          { (user?.active_organization?.copyright_text && body && body !== '') &&
            <div style={{marginTop: '16px', padding: '8px', borderTop: '1px solid rgba(240, 240, 240, 0.2)'}}>
              <span style={{fontSize: '14px', color: '#f0f0f0'}}>
                © {new Date().getFullYear()} {user.active_organization.copyright_text}
              </span>
            </div>
          }

          <div style={{display: 'flex', flexDirection: 'row', flexWrap: 'wrap', paddingTop: '16px', minHeight: '70px'}}>
            <div style={{margin: '6px'}}>
              <Button onPress={cancelPreview} color="secondary" variant="bordered">
                OK
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default CampaignPreview;
